import React, { useEffect, useCallback } from 'react';

export interface ActiveSubscriptionInfo {
  id: string;
  planName: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export interface ActiveSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: ActiveSubscriptionInfo | null;
}

const ActiveSubscriptionModal: React.FC<ActiveSubscriptionModalProps> = ({
  isOpen,
  onClose,
  subscription,
}) => {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen || !subscription) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="active-subscription-title"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Header with Icon */}
        <div className="pt-8 pb-4 flex justify-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center border-2 border-green-500/30">
            <svg
              className="w-10 h-10 text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 pb-6 text-center">
          <h2 id="active-subscription-title" className="text-2xl font-bold text-white mb-3">
            You're Already Subscribed!
          </h2>
          <p className="text-slate-300 mb-6">
            Great news! You already have an active subscription to TradeRewind.
          </p>

          {/* Subscription Details Card */}
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-5 mb-6 border border-green-500/20">
            <div className="flex items-center justify-center gap-2 mb-4">
              <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-green-400 font-semibold text-lg">{subscription.planName}</span>
            </div>
            
            <div className="space-y-3 text-left">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Status</span>
                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full uppercase">
                  {subscription.status}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Renews On</span>
                <span className="text-white text-sm font-medium">
                  {formatDate(subscription.currentPeriodEnd)}
                </span>
              </div>
              
              {subscription.cancelAtPeriodEnd && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Note</span>
                  <span className="text-amber-400 text-xs">
                    Cancels at period end
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Info Message */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-700">
            <div className="flex items-start gap-3 text-left">
              <svg
                className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm text-slate-300">
                  Your subscription is active and all premium features are available.
                </p>
                <p className="text-sm text-slate-400 mt-2">
                  To change your plan or manage billing, visit your account billing section.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-800/50 border-t border-slate-700 px-8 py-4">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl font-semibold transition-all"
          >
            Continue to TradeRewind
          </button>
          <p className="text-center text-xs text-slate-500 mt-3">
            Thank you for being a valued subscriber!
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActiveSubscriptionModal;
